import React from 'react';
import { ArrowRight, Lock, Percent, WalletCards } from 'lucide-react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import styles from './MembershipManager.module.css';
import useAccess from '../../../../../hooks/useAccess';
import PrepaidMembershipManager from './PrepaidMembershipManager/PrepaidMembershipManager';
import DiscountMembershipManager from './DiscountMembershipManager/DiscountMembershipManager';

// =============================================================================
// Membership plans (module "memberships" in the access blueprint)
//   Discount plans : view_discount, create_discount, edit_discount, deactivate_discount
//   Prepaid plans  : view_prepaid,  create_prepaid,  edit_prepaid,  deactivate_prepaid
// The owner (HQ) can do everything. Everyone else, including branch admins, follows their access
// (own access limited by the HQ branch limit). The server checks the same rules.
// =============================================================================
const BASE = '/manage-loyalty-program/membership';

export default function MembershipManager() {
    const navigate = useNavigate();
    const { can, isOwner } = useAccess();

    const discount = {
        view: isOwner || can('memberships.view_discount'),
        canCreate: isOwner || can('memberships.create_discount'),
        canEdit: isOwner || can('memberships.edit_discount'),
        canDeactivate: isOwner || can('memberships.deactivate_discount'),
    };
    const prepaid = {
        view: isOwner || can('memberships.view_prepaid'),
        canCreate: isOwner || can('memberships.create_prepaid'),
        canEdit: isOwner || can('memberships.edit_prepaid'),
        canDeactivate: isOwner || can('memberships.deactivate_prepaid'),
    };

    if (!discount.view && !prepaid.view) {
        return (
            <div className={styles.managerWrapper}>
                <div style={{ display: 'grid', placeItems: 'center', minHeight: 320, padding: 24, textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
                    <div>
                        <Lock size={28} color="#797980" />
                        <h3 style={{ margin: '10px 0 4px', fontSize: 16, fontWeight: 900, color: '#18181a' }}>You cannot see membership plans</h3>
                        <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: '#797980' }}>Ask your manager to give you access to discount or prepaid plans.</p>
                    </div>
                </div>
            </div>
        );
    }

    const back = () => navigate(BASE);
    const onlyOne = discount.view !== prepaid.view;   // only one kind allowed: open it directly

    const SelectorScreen = () => (
        <div className={styles.selectorContainer}>
            <div className={styles.selectorHeader}>
                <h2>Select Membership Type</h2>
                <p>Choose the type of membership system you want to manage or create.</p>
            </div>

            <div className={styles.cardGrid}>
                {discount.view && (
                    <button type="button" className={styles.typeCard} onClick={() => navigate(`${BASE}/discount`)}>
                        <div className={`${styles.iconWrapper} ${styles.iconDiscount}`}><Percent size={24} /></div>
                        <div className={styles.cardContent}>
                            <h3>Discount Memberships</h3>
                            <p>Profile-based subscriptions. Clients pay a fee to get flat percentage discounts on services and products.</p>
                        </div>
                        <div className={styles.cardAction}>{discount.canCreate || discount.canEdit ? 'Manage discounts' : 'View discounts'} <ArrowRight size={16} /></div>
                    </button>
                )}
                {prepaid.view && (
                    <button type="button" className={styles.typeCard} onClick={() => navigate(`${BASE}/prepaid`)}>
                        <div className={`${styles.iconWrapper} ${styles.iconPrepaid}`}><WalletCards size={24} /></div>
                        <div className={styles.cardContent}>
                            <h3>Prepaid Memberships</h3>
                            <p>Wallet-based inventory system. Generate secure prepaid cards to hand over to clients.</p>
                        </div>
                        <div className={styles.cardAction}>{prepaid.canCreate || prepaid.canEdit ? 'Manage prepaid cards' : 'View prepaid cards'} <ArrowRight size={16} /></div>
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className={styles.managerWrapper}>
            <Routes>
                <Route index element={onlyOne ? <Navigate to={discount.view ? 'discount' : 'prepaid'} replace /> : <SelectorScreen />} />
                <Route
                    path="discount/*"
                    element={discount.view
                        ? <DiscountMembershipManager canCreate={discount.canCreate} canEdit={discount.canEdit} canDeactivate={discount.canDeactivate} onBack={onlyOne ? () => navigate('/manage-loyalty-program') : back} />
                        : <Navigate to={BASE} replace />}
                />
                <Route
                    path="prepaid/*"
                    element={prepaid.view
                        ? <PrepaidMembershipManager canCreate={prepaid.canCreate} canEdit={prepaid.canEdit} canDeactivate={prepaid.canDeactivate} onBack={onlyOne ? () => navigate('/manage-loyalty-program') : back} />
                        : <Navigate to={BASE} replace />}
                />
                <Route path="*" element={<Navigate to={BASE} replace />} />
            </Routes>
        </div>
    );
}